import { useEffect, useId, useRef } from 'react';
import { ModalProps } from '@msc-b2c/interfaces/types';
import './modal.module.scss';
import { Icon } from '../icons/Icon';
import { Text } from '../text/Text';
import clsx from 'clsx';

/**
 * Stack of currently open modals. Only the most recently opened modal
 * enforces the focus trap, so a modal opened on top of another takes the
 * trap over and gives it back when it closes. Without this, two mounted
 * modals would fight over focus in an infinite focusin loop.
 */
const modalStack: HTMLElement[] = [];

export const Modal = ({
  id,
  children,
  styleOption,
  onClose,
  headerTitle,
  modalIcon,
  shouldRenderBackdrop = false,
  iconStyleOption,
  hideCloseIcon = false,
  ariaLabelledBy,
  ariaDescribedBy,
  initialFocusRef,
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const generatedId = useId();
  const baseId = id ?? generatedId;
  const titleId = ariaLabelledBy ?? (headerTitle ? `${baseId}-title` : undefined);

  useEffect(() => {
    const container = modalRef.current;
    if (!container) {
      return;
    }

    const previouslyFocused = document.activeElement as HTMLElement | null;
    modalStack.push(container);

    if (initialFocusRef?.current) {
      initialFocusRef.current.focus({ preventScroll: true });
    } else if (initialFocusRef) {
      container.focus({ preventScroll: true });
    } else {
      const labelledByElement = ariaLabelledBy ? document.getElementById(ariaLabelledBy) : null;
      const headerTitleElement =
        !labelledByElement && headerTitle && titleId ? document.getElementById(titleId) : null;
      const focusTarget = labelledByElement ?? headerTitleElement;

      if (focusTarget instanceof HTMLElement) {
        if (!focusTarget.hasAttribute('tabindex')) {
          focusTarget.setAttribute('tabindex', '-1');
        }
        focusTarget.focus({ preventScroll: true });
      } else {
        container.focus({ preventScroll: true });
      }
    }

    // Lock background scrolling while the modal is open. If the drawer already
    // owns scroll lock via body.-overlay, do not override inline overflow to
    // avoid lock/unlock interference when this top modal closes.
    const shouldManageBodyOverflow = !document.body.classList.contains('-overlay');
    const previousBodyOverflow = document.body.style.overflow;
    if (shouldManageBodyOverflow) {
      document.body.style.overflow = 'hidden';
    }

    // Focus trap: if focus escapes the modal (Tab past the last element or
    // Shift+Tab before the first), pull it back to the modal container so
    // the next Tab continues from its first focusable child.
    const keepFocusInside = (event: FocusEvent) => {
      const isTopMostModal = modalStack[modalStack.length - 1] === container;
      if (!isTopMostModal || container.contains(event.target as Node)) {
        return;
      }
      // Only reclaim focus that actually left from inside this modal
      // (relatedTarget is the element that lost focus); never steal focus
      // that was already elsewhere, e.g. inside another modal.
      if (!container.contains(event.relatedTarget as Node)) {
        return;
      }
      container.focus({ preventScroll: true });
    };

    document.addEventListener('focusin', keepFocusInside);

    return () => {
      document.removeEventListener('focusin', keepFocusInside);
      const index = modalStack.lastIndexOf(container);
      if (index !== -1) {
        modalStack.splice(index, 1);
      }
      if (shouldManageBodyOverflow) {
        document.body.style.overflow = previousBodyOverflow;
      }
      previouslyFocused?.focus?.({ preventScroll: true });
    };
  }, [ariaLabelledBy, headerTitle, titleId, initialFocusRef]);

  return (
    <>
      {shouldRenderBackdrop && (
        <div
          className={'cmp-container__modal-backdrop'}
          data-backdrop-open={true}
          aria-hidden="true"
        />
      )}
      <div
        ref={modalRef}
        id={id}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={ariaDescribedBy}
        tabIndex={-1}
        className={clsx('cmp-container__modal --react-modal', styleOption)}
      >
        {headerTitle ? (
          <>
            <div className={'cmp-container__modal-header-wrapper'}>
              <div className={'cmp-container__modal-header'}>
                <Text
                  id={titleId}
                  tagHtml={'h3'}
                  text={headerTitle}
                  styleOption={'title cmp-text__big'}
                />
                {shouldRenderBackdrop && (
                  <Icon
                    icon={modalIcon ?? ''}
                    onClick={onClose}
                    ariaLabel="Close"
                  />
                )}
              </div>
            </div>
            <div className="cmp-container__modal-body">
              <div className={'cmp-container'}>{children}</div>
            </div>
          </>
        ) : (
          <div className="cmp-container__modal-body">
            {!hideCloseIcon && (
              <Icon
                icon={modalIcon ?? 'close'}
                onClick={onClose}
                styleOption={iconStyleOption}
                ariaLabel="Close"
              />
            )}
            <div className={'cmp-container'}>{children}</div>
          </div>
        )}
      </div>
    </>
  );
};
